import axios from "axios";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  List,
} from "@mui/material";
import localize from "../../../common/localize";
import {
  PopperFullWidth,
  getInputLabelProps,
  getInputProps,
} from "../../../common/appStyles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Combobox from "../../../components/common/Combobox";
import { Add, Lock, LockOpen } from "@mui/icons-material";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getClearDailyRouteEvent } from "../../../common/EntitiesManagement";
import {
  GetDateInYYYYMMDDWithoutTimeFormat,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import EventSearch from "../../../components/common/EventSearch";
import AddObservation from "./AddObservation";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import ModalBackground from "../../../components/common/ModalBackground";
import ErrorMessage from "../../../components/common/ErrorMessage";
import NotificationItem from "../../../components/common/NotificationItem";
import DailyRoutEventItem from "./DailyRouteEventItem";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import ExpandableView from "../../../components/common/ExpandableView";

export default function AddOrEditDailyRoute({
  dailyRoute,
  setDailyRoute,
  handleUpdateRequest,
  handleCreateRequest,
  showCreateConfirmation,
  setShowCreateConfirmation,
  updateRouteEvents,
  setUpdateRouteEvents,
  deletedRouteEvents,
  setDeletedRouteEvents,
  isReadOnly,
}) {
  const [routeEventToDelete, setRouteEventToDelete] = useState(undefined);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAddObservation, setShowAddObservation] = useState(false);
  const [dailyRouteEventToEdit, setDailyRouteEventToEdit] = useState();
  const {
    DAILY_ROUTE_ENDPOINT,
    EVENTS_API,
    PERSONAL_API,
    toggleVisibleModalConfirm,
    config,
  } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [searchList, setSearchList] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [personalData, setPersonalData] = useState([]);

  const getPersonal = async () => {
    const configData = {
      headers: {
        ...config.headers,
        pagination: -1,
      },
    };

    const personel = await axios.get(`${PERSONAL_API}`, configData);

    if (personel?.data?.Data?.length > 0) {
      setPersonalData(...[personel.data.Data]);
    }
  };

  useEffect(() => {
    if (searchList?.length > 0) {
      const newEvents = [];
      searchList.forEach((event) => {
        if (
          event &&
          dailyRoute.RouteEvents?.findIndex(
            (x) => x.EventInfo.Id === event.Id
          ) < 0
        ) {
          newEvents.push(getClearDailyRouteEvent(event));
        }
      });

      if (newEvents.length > 0) {
        setDailyRoute({
          ...dailyRoute,
          RouteEvents: [...dailyRoute.RouteEvents, ...newEvents],
        });
      }

      setSearchList([]);
    }
  }, [searchList]);

  const handleRouteName = (e) => {
    setDailyRoute({
      ...dailyRoute,
      Name: e.target.value,
    });
  };

  const handleScheduleDateChange = (dateValue) => {
    if (!dateValue) {
      const newDate = new Date();
      setDailyRoute({
        ...dailyRoute,
        RouteDate: GetDateInYYYYMMDDWithoutTimeFormat(newDate, false, false),
        RouteDateValue: newDate,
      });
      return;
    }

    setDailyRoute({
      ...dailyRoute,
      RouteDate: GetDateInYYYYMMDDWithoutTimeFormat(dateValue, false, false),
      RouteDateValue: dateValue,
    });
  };

  const handlePersonalAssignedChange = (currentPersonal) => {
    if (!currentPersonal) {
      setDailyRoute({
        ...dailyRoute,
        PersonalAssigned: null,
        PersonalId: 0,
      });
      return;
    }

    setDailyRoute({
      ...dailyRoute,
      PersonalAssigned: currentPersonal,
      PersonalId: currentPersonal.id,
    });
  };

  const handleIsPrivateChange = (e) => {
    setDailyRoute({
      ...dailyRoute,
      [e.target.name]: !e.target.checked,
    });
  };

  const handleChange = (e) => {
    if (e.target.name === "Observation") {
      setDescriptionCount(e.target.value.length);
    }

    setDailyRoute({ ...dailyRoute, [e.target.name]: e.target.value });
  };

  const getEventsByExpectedSolutionDate = async (expectedDate) => {
    const configData = {
      headers: {
        ...config.headers,
        Filter: JSON.stringify([
          {
            Name: "ExpectedSolutionDate",
            FilterValue: {
              Value: expectedDate,
              AlternateValue: expectedDate.replace("00:00:00", "23:59:59"),
            },
            FriendlyName: "ExpectedSolutionDate",
            Value: expectedDate,
          },
        ]),
      },
    };

    const events = await axios.get(
      `${EVENTS_API}/byExpectedSolutionDate`,
      configData
    );

    if (events?.data?.Data?.length > 0) {
      setDailyRoute({
        ...dailyRoute,
        RouteEvents: events.data.Data.map((eventData) =>
          getClearDailyRouteEvent(eventData)
        ),
      });
    }
  };

  const getScheduledEvents = async (id) => {
    const getResult = await axios.get(
      `${DAILY_ROUTE_ENDPOINT}/${id}/events`,
      config
    );

    if (getResult?.data?.Data) {
      setDailyRoute({
        ...dailyRoute,
        RouteEvents: getResult?.data?.Data,
      });
    }
  };

  useEffect(() => {
    getPersonal();
  }, []);

  useEffect(() => {
    if (dailyRoute?.Id > 0) {
      getScheduledEvents(dailyRoute?.Id);
    } else {
      getEventsByExpectedSolutionDate(dailyRoute.RouteDate);
    }
  }, [dailyRoute?.Id]);

  const removeEventFromList = (routeEvent) => {
    setRouteEventToDelete(routeEvent);
    setShowDeleteConfirmation(true);
    toggleVisibleModalConfirm();
  };

  const getDailyRouteInformation = () => {
    return (
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginTop: "10px",
          flexFlow: "column",
        }}
      >
        <FormControlLabel
          size="small"
          name="IsPrivate"
          disabled={isReadOnly}
          control={
            <Checkbox
              size="small"
              name="IsPrivate"
              checked={dailyRoute?.IsPrivate === false}
              onChange={handleIsPrivateChange}
              icon={<Lock />}
              checkedIcon={<LockOpen />}
            />
          }
          label={
            <PageTitleSubtitle
              useEventTitleStyle
              useFieldTitleStyle
              title={
                dailyRoute?.IsPrivate ? localize.is_private : localize.is_public
              }
              subtitle={
                dailyRoute?.IsPrivate
                  ? localize.is_private_complement
                  : localize.is_public_complement
              }
            />
          }
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={isReadOnly}
            label={localize.route_date}
            className="w-full"
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            PopperComponent={PopperFullWidth}
            value={dailyRoute?.RouteDateValue ?? new Date()}
            onChange={handleScheduleDateChange}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  placeholder: "",
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  style: {
                    fontSize: "1em",
                    paddingBottom: "10px",
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>

        <TextField
          aria-readonly={isReadOnly}
          required
          InputLabelProps={getInputLabelProps()}
          inputProps={getInputProps()}
          size="small"
          fullWidth
          id="outlined-basic"
          name="Name"
          label={localize.name}
          variant="outlined"
          onChange={isReadOnly ? undefined : handleRouteName}
          value={dailyRoute?.Name}
        />

        <Combobox
          disabled={isReadOnly}
          id="PersonalAssigned"
          fieldLabel={localize.assigned_to}
          fieldOptions={personalData ?? []}
          fieldValue={dailyRoute?.PersonalAssigned}
          customOptionLabel={(option) =>
            `${option.Identification} - ${option.SurName} ${option.LastName}`
          }
          handleValueChange={handlePersonalAssignedChange}
          popperFullWidth={PopperFullWidth}
        />

        <div className="flex flex-wrap">
          <div className="w-full">
            <TextField
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                ...getInputProps(),
                maxLength: maximumCharactersForDescription,
              }}
              size="small"
              fullWidth
              multiline
              rows={4}
              id="outlined-basic"
              name="Observation"
              label={localize.description}
              variant="outlined"
              onChange={isReadOnly ? undefined : handleChange}
              value={dailyRoute?.Observation ?? ""}
              helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
            />
          </div>
        </div>

        <Box className="w-full flex">
          <Box className="two_columns_grid">
            <label className="event_detail_description">
              <b>{localize.created_by}:</b>
            </label>
            <label className="event_detail_description">{dailyRoute.CreatedBy}</label>
          </Box>
        </Box>
      </div>
    );
  };

  const getOrdersData = () => {
    return (
      <div className="flex flex-col gap-2">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <Alert className="w-full" severity="warning">
            <label className="text-xs">
              {localize.daily_route_service_orders}
            </label>
          </Alert>
          <Button
            sx={{ width: "111px", minWidth: "111px" }}
            size="medium"
            variant="contained"
            color="success"
            className="app_general_button_data app_success_button btn-sm "
            onClick={() => {
              setSearchList([]);
              setShowSearch(true);
            }}
          >
            <Add style={{ width: "16px", height: "16px" }} />
            <div className="mr-1" />
            {localize.add}
          </Button>
        </div>

        <Divider />

        <List className="w-full flex flex-col gap-2">
          {dailyRoute?.RouteEvents?.length > 0 ? (
            dailyRoute.RouteEvents.map((event) => (
              <DailyRoutEventItem
                item={event}
                onEditObservation={() => {
                  setDailyRouteEventToEdit(event);
                  setShowAddObservation(true);
                }}
                onDelete={() => {
                  removeEventFromList(event);
                }}
              />
            ))
          ) : (
            <NotificationItem notification={{ message: localize.no_data }} />
          )}
        </List>
      </div>
    );
  };

  const removeRouteEvent = (routeEvent) => {
    if (routeEvent) {
      const index = dailyRoute.RouteEvents.findIndex(
        (element) => element.Id === routeEvent.Id
      );

      if (index >= 0) {
        if (!routeEvent.IsNew) {
          setDeletedRouteEvents([...deletedRouteEvents, routeEvent]);
        }

        const routeEvents = dailyRoute.RouteEvents;
        routeEvents.splice(index, 1);
        setDailyRoute({
          ...dailyRoute,
          RouteEvents: [...routeEvents],
        });
      }
    }
  };

  return (
    <div className="flex flex-col lateral-padding h-full">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showDeleteConfirmation && (
        <DeleteConfirmationMessage
          entityType={localize.service_order}
          entityName={routeEventToDelete.EventInfo?.Code}
          onClickForCancel={() => {
            setRouteEventToDelete(undefined);
            setShowDeleteConfirmation(false);
            toggleVisibleModalConfirm();
          }}
          onClickForConfirm={() => {
            removeRouteEvent(routeEventToDelete);
            setShowDeleteConfirmation(false);
            toggleVisibleModalConfirm();
          }}
        />
      )}

      {showSearch && (
        <EventSearch
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          setResultList={setSearchList}
        />
      )}

      {showAddObservation && (
        <AddObservation
          show={showAddObservation}
          setShow={setShowAddObservation}
          dailyRoute={dailyRoute}
          setDailyRoute={setDailyRoute}
          dailyRouteEvent={dailyRouteEventToEdit}
          routeEventsToUpdate={updateRouteEvents}
          setRouteEventsToUpdate={setUpdateRouteEvents}
        />
      )}

      {showCreateConfirmation && (
        <ModalBackground
          title={localize.new_route}
          showModal={showCreateConfirmation}
          setShowModal={setShowCreateConfirmation}
          Component={<label>{localize.question_save_changes}</label>}
          onClickToCancel={() => {
            setShowCreateConfirmation(false);
          }}
          onClickToSubmit={() => {
            if (dailyRoute?.Id > 0) {
              handleUpdateRequest(dailyRoute);
            } else {
              handleCreateRequest(dailyRoute);
            }

            setShowCreateConfirmation(false);
          }}
        />
      )}

      <Box className="w-full">
        <ExpandableView
          components={[
            {
              Name: localize.general_information,
              Component: getDailyRouteInformation(),
            },
            {
              Name: `${localize.service_orders} (${
                dailyRoute?.RouteEvents?.length ?? 0
              })`,
              Component: getOrdersData(),
            },
          ]}
        />
      </Box>
    </div>
  );
}
